import { StyledMUISecondaryButton as Button } from '@components/Button';
import { UpdateSalesBOMForm } from '@components/MatchReport/UpdateSalesBOMForm';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, Typography } from '@mui/material';
import { HeaderText } from '@pages/CreateNewProductPage/styles';
import {
  salesBOMFormSchema,
  type SalesBOMFormSchema,
} from '@partful/pages/create-product/sales-bom/lib/SalesBOMFormSchema';
import { noop } from '@partful/shared/lib/noop';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { GetConfigurationMatchReportResponse } from 'src/services/productInventory';
import { Column, Item, List } from './StyledComponents';

interface UpdateMatchReportCommonProps {
  unsuccessfullyMatched: GetConfigurationMatchReportResponse['unsuccessfullyMatched'];
  configurationPID: string;
  salesBOMJobRunID: string;
  cadJobRunID?: string;
}

// if upload provided, do not provide productName
interface UpdateMatchReportWithUploadProps extends UpdateMatchReportCommonProps {
  productName?: never;
  onSalesBOMUploadedSuccessfully?: () => Promise<void>;
  isComplete?: boolean;
  onSalesBOMUpload?: () => void;
}

// if productName provided, do not provide upload
interface UpdateMatchReportWithProductNameProps extends UpdateMatchReportCommonProps {
  productName?: string;
  onSalesBOMUploadedSuccessfully?: never;
  isComplete?: never;
  onSalesBOMUpload?: never;
}

/**
 * Discriminated union:
 * - If `productName` provided, do not provide `upload`
 * - If `upload` provided, do not provide `productName`
 */
type UpdateMatchReportProps = UpdateMatchReportWithProductNameProps | UpdateMatchReportWithUploadProps;

export function UpdateMatchReport(props: Readonly<UpdateMatchReportProps>) {
  const { t } = useTranslation(['createProduct', 'manageProduct']);

  const {
    productName,
    unsuccessfullyMatched,
    configurationPID,
    salesBOMJobRunID,
    cadJobRunID,
    onSalesBOMUploadedSuccessfully,
    isComplete,
    onSalesBOMUpload = noop,
  } = props;

  const form = useForm<SalesBOMFormSchema>({
    resolver: zodResolver(salesBOMFormSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (form.formState.isSubmitting) {
      onSalesBOMUpload();
    }
  }, [form.formState.isSubmitting, onSalesBOMUpload]);

  const description =
    unsuccessfullyMatched !== 0
      ? t('createProduct:matchReport.updateSalesBomText', { unsuccessfullyMatched })
      : /* istanbul ignore next */
        t('manageProduct:salesBOM.matchReportStatus.fullyMatched');
  const showUpdateSalesBOM = onSalesBOMUploadedSuccessfully !== undefined;

  return (
    <Column>
      <List>
        <Item alert={unsuccessfullyMatched !== 0} alignItems="start" spacing={1.5} sx={{ padding: 3 }}>
          <li>
            <HeaderText variant="body1">Update Sales BOM data</HeaderText>
            <Typography>{description}</Typography>
            {showUpdateSalesBOM ? (
              <FormProvider {...form}>
                <UpdateSalesBOMForm
                  configurationPID={configurationPID}
                  onSalesBOMUploadedSuccessfully={onSalesBOMUploadedSuccessfully}
                />
                {isComplete && (
                  /* istanbul ignore next - this will be better test in playwright */ <Stack
                    direction={{ xs: 'row' }}
                    spacing={2}
                    useFlexGap
                  >
                    <FontAwesomeIcon data-testid="status-icon" icon={faCheck} />
                    <Typography>{t('sbom.success.processedSuccess')}</Typography>
                  </Stack>
                )}
              </FormProvider>
            ) : (
              <Button
                color="primary"
                href={`/manage-product/${productName}/sales-bom?configurationPID=${configurationPID}&salesBOMJobRunID=${salesBOMJobRunID}&cadJobRunID=${cadJobRunID}&productName=${productName}`}
                data-testid="manage-sales-bom-button"
              >
                {t('createProduct:matchReport.manageSalesBom')}
              </Button>
            )}
          </li>
        </Item>
        <li>
          {!showUpdateSalesBOM && (
            <>
              <Typography>
                <Trans i18nKey="createProduct:matchReport.browseProducts" />
              </Typography>
              <Button color="primary" href="/product-library">
                {t('createProduct:matchReport.visitProductLibrary')}
              </Button>
            </>
          )}
        </li>
      </List>
    </Column>
  );
}
